// Cliente HTTP del módulo de Seguimiento judicial.

import {
  LOGIN_REDIRECT_URL,
  SEGUIMIENTO_CONSULTA_ACTIVAR_URL,
  SEGUIMIENTO_CONSULTA_INICIAR_URL,
  SEGUIMIENTO_CONSULTA_REINTENTAR_URL,
  SEGUIMIENTO_SEGUIMIENTOS_ENDPOINT,
  getConsultaEstadoUrl,
  getConsultaProcesoUrl,
  getConsultaRecienteUrl,
  getConsultaResultadosUrl,
  getNovedadesUrl,
  getProcesoDetalleUrl,
  getSeguimientoDetailUrl,
  getSeguimientoRevisarUrl,
  getSeguirProcesoUrl,
} from '../../externalUrls';
import { getAuthHeaders } from '../../lib/utils';
import type {
  ActivarConsultaResponse,
  ActuacionesConsultaResponse,
  ConsultaInfo,
  CrearSeguimientoResponse,
  EstadoConsultaResponse,
  IniciarConsultaResponse,
  NovedadesResponse,
  ProcesoDetalleResponse,
  RecienteConsultaResponse,
  ResultadosConsultaResponse,
  Seguimiento,
  SeguimientoDetalle,
  SeguimientoListResponse,
} from './types';

export class SeguimientoApiError extends Error {
  constructor(readonly status: number, message?: string) {
    super(message || `La solicitud falló con HTTP ${status}.`);
    this.name = 'SeguimientoApiError';
  }
}

export function mensajeError(error: unknown): string {
  if (error instanceof SeguimientoApiError) return error.message;
  return 'No se pudo completar la operación. Inténtalo nuevamente.';
}

function redirectIfSessionExpired(status: number): void {
  if (status === 401 || status === 403) {
    window.location.assign(LOGIN_REDIRECT_URL);
  }
}

async function requestJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
  });

  if (!response.ok) {
    redirectIfSessionExpired(response.status);
    let detail: string | undefined;
    try {
      const data = await response.json();
      if (data && typeof data.detail === 'string') {
        detail = data.detail;
      }
    } catch {
      // Sin cuerpo JSON utilizable: se usa el mensaje por defecto.
    }
    throw new SeguimientoApiError(response.status, detail);
  }

  return (await response.json()) as T;
}

function conJson(method: string, body?: unknown): RequestInit {
  return {
    method,
    headers: {
      ...getAuthHeaders(),
      Accept: 'application/json',
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  };
}

// ── Seguimientos ─────────────────────────────────────────────

export function listSeguimientos(): Promise<SeguimientoListResponse> {
  return requestJson<SeguimientoListResponse>(SEGUIMIENTO_SEGUIMIENTOS_ENDPOINT, {
    headers: { Accept: 'application/json' },
  });
}

export function crearSeguimientoRadicado(
  radicado: string,
  correoActivo: boolean,
): Promise<CrearSeguimientoResponse> {
  return requestJson<CrearSeguimientoResponse>(
    SEGUIMIENTO_SEGUIMIENTOS_ENDPOINT,
    conJson('POST', { tipo: 'radicado', radicado, correo_activo: correoActivo }),
  );
}

export function crearSeguimientoNombre(
  nombre: string,
  correoActivo: boolean,
): Promise<CrearSeguimientoResponse> {
  return requestJson<CrearSeguimientoResponse>(
    SEGUIMIENTO_SEGUIMIENTOS_ENDPOINT,
    conJson('POST', { tipo: 'nombre', nombre, correo_activo: correoActivo }),
  );
}

export function getSeguimiento(id: number): Promise<SeguimientoDetalle> {
  return requestJson<SeguimientoDetalle>(getSeguimientoDetailUrl(id), {
    headers: { Accept: 'application/json' },
  });
}

export function actualizarSeguimiento(
  id: number,
  cambios: { pausado?: boolean; correo_activo?: boolean },
): Promise<Seguimiento> {
  return requestJson<Seguimiento>(getSeguimientoDetailUrl(id), conJson('PATCH', cambios));
}

export function eliminarSeguimiento(id: number, soloPendiente = false): Promise<{ ok: boolean }> {
  const url = getSeguimientoDetailUrl(id) + (soloPendiente ? '?solo_pendiente=1' : '');
  return requestJson<{ ok: boolean }>(url, conJson('DELETE'));
}

export function revisarSeguimiento(id: number): Promise<{ ok: boolean; estado: string }> {
  return requestJson<{ ok: boolean; estado: string }>(
    getSeguimientoRevisarUrl(id),
    conJson('POST', {}),
  );
}

export function seguirProceso(
  seguimientoId: number,
  procesoId: number,
): Promise<CrearSeguimientoResponse> {
  return requestJson<CrearSeguimientoResponse>(
    getSeguirProcesoUrl(seguimientoId, procesoId),
    conJson('POST', {}),
  );
}

// ── Consultas progresivas ────────────────────────────────────

export function iniciarConsulta(
  tipo: 'radicado' | 'nombre',
  valor: string,
): Promise<IniciarConsultaResponse> {
  const body = tipo === 'radicado'
    ? { tipo, radicado: valor }
    : { tipo, nombre: valor };
  return requestJson<IniciarConsultaResponse>(
    SEGUIMIENTO_CONSULTA_INICIAR_URL,
    conJson('POST', body),
  );
}

export function estadoConsulta(id: number): Promise<EstadoConsultaResponse> {
  return requestJson<EstadoConsultaResponse>(getConsultaEstadoUrl(id), {
    headers: { Accept: 'application/json' },
  });
}

export function resultadosConsulta(
  id: number,
  pagina = 1,
  orden: 'estable' | 'actuacion' = 'estable',
): Promise<ResultadosConsultaResponse> {
  return requestJson<ResultadosConsultaResponse>(
    getConsultaResultadosUrl(id, pagina, orden),
    { headers: { Accept: 'application/json' } },
  );
}

export function reintentarConsulta(
  id: number,
): Promise<{ ok: boolean; consulta: ConsultaInfo }> {
  return requestJson<{ ok: boolean; consulta: ConsultaInfo }>(
    SEGUIMIENTO_CONSULTA_REINTENTAR_URL,
    conJson('POST', { id }),
  );
}

export function activarConsulta(
  id: number,
  correoActivo: boolean,
): Promise<ActivarConsultaResponse> {
  return requestJson<ActivarConsultaResponse>(
    SEGUIMIENTO_CONSULTA_ACTIVAR_URL,
    conJson('POST', { id, correo_activo: correoActivo }),
  );
}

export function consultaReciente(
  tipo: 'radicado' | 'nombre',
): Promise<RecienteConsultaResponse> {
  return requestJson<RecienteConsultaResponse>(getConsultaRecienteUrl(tipo), {
    headers: { Accept: 'application/json' },
  });
}

export function consultarActuacionesPreview(
  idProceso: number,
  pagina = 1,
): Promise<ActuacionesConsultaResponse> {
  return requestJson<ActuacionesConsultaResponse>(
    getConsultaProcesoUrl(idProceso, pagina),
    { headers: { Accept: 'application/json' } },
  );
}

// ── Novedades y procesos ─────────────────────────────────────

export function listNovedades(pagina = 1): Promise<NovedadesResponse> {
  return requestJson<NovedadesResponse>(getNovedadesUrl(pagina), {
    headers: { Accept: 'application/json' },
  });
}

export function getProceso(
  procesoId: number,
  pagina = 1,
): Promise<ProcesoDetalleResponse> {
  return requestJson<ProcesoDetalleResponse>(getProcesoDetalleUrl(procesoId, pagina), {
    headers: { Accept: 'application/json' },
  });
}
